///
/// EarthBrowser
///
/// Created by Matt Giger on 4/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useEffect, useCallback } from "react"
import { useAppDispatch } from "@/redux/store"
import { setOnboardingOpen } from "@/redux/dialogSlice"

const ONBOARDING_KEY_PREFIX = "earthbrowser_onboarding_complete_"
const NEW_USER_THRESHOLD_MS = 5 * 60 * 1000

interface UserProfile {
	id: number
	created_at: string
}

function getStorageKey(userId: number): string {
	return `${ONBOARDING_KEY_PREFIX}${userId}`
}

function hasCompletedOnboarding(userId: number): boolean {
	try {
		return localStorage.getItem(getStorageKey(userId)) === "true"
	} catch {
		return false
	}
}

export function markOnboardingComplete(userId: number): void {
	try {
		localStorage.setItem(getStorageKey(userId), "true")
	} catch (error) {
		console.error("Failed to mark onboarding complete:", error)
	}
}

export function resetOnboardingForUser(userId: number): void {
	try {
		localStorage.removeItem(getStorageKey(userId))
	} catch (error) {
		console.error("Failed to reset onboarding:", error)
	}
}

/**
 * Automatically shows the onboarding dialog for new users.
 *
 * Detection strategies:
 * 1. Query parameter: ?welcome=true (from OAuth redirects)
 * 2. Account age: account created within last 5 minutes
 * 3. localStorage: check if onboarding was already completed for this user
 */
export function useOnboarding(userProfile: UserProfile | undefined) {
	const dispatch = useAppDispatch()

	useEffect(() => {
		if (!userProfile) return

		const urlParams = new URLSearchParams(window.location.search)
		const welcomeParam = urlParams.get("welcome")

		if (welcomeParam === "true") {
			dispatch(setOnboardingOpen(true))

			urlParams.delete("welcome")
			const newUrl = urlParams.toString()
				? `${window.location.pathname}?${urlParams.toString()}`
				: window.location.pathname
			window.history.replaceState({}, "", newUrl)

			return
		}

		if (hasCompletedOnboarding(userProfile.id)) {
			return
		}

		const accountCreatedAt = new Date(userProfile.created_at)
		const now = new Date()
		const accountAge = now.getTime() - accountCreatedAt.getTime()

		if (accountAge < NEW_USER_THRESHOLD_MS) {
			dispatch(setOnboardingOpen(true))
		}
	}, [userProfile, dispatch])

	const completeOnboarding = useCallback(() => {
		if (userProfile) {
			markOnboardingComplete(userProfile.id)
		}
	}, [userProfile])

	return { completeOnboarding }
}
